import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, message as antdMessage, Image } from 'antd';
import { UserOutlined, CopyOutlined, DownOutlined, PlayCircleOutlined, SoundOutlined, AudioFilled, StopOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useChatStore, Message } from '../../../store/chatStore';
import { useAuthStore } from '../../../store/authStore';
import { getMediaUrl } from '../../../utils/request';
import { SystemSettings } from '../../../api/public';
import { speechService } from '../../../api/speech';
import './ChatArea.css';

export const MijiaAvatarSvg: React.FC<{ size: number; className?: string; interactive?: boolean }> = ({ size, className, interactive }) => {
  return (
    <svg
      className={`${className} ${interactive ? 'interactive-avatar' : ''}`}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="mijiaFaceStatic" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6EE7B7" />
          <stop offset="100%" stopColor="#22C55E" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="16" fill="url(#mijiaFaceStatic)" />
      
      {/* 眉毛 */}
      <path className={`ai-eyebrow left ${interactive ? 'eyebrow-move' : ''}`} d="M18 20 Q 24 18, 30 20" fill="none" stroke="#0B1220" strokeWidth="2.5" strokeLinecap="round" />
      <path className={`ai-eyebrow right ${interactive ? 'eyebrow-move' : ''}`} d="M34 20 Q 40 18, 46 20" fill="none" stroke="#0B1220" strokeWidth="2.5" strokeLinecap="round" />
      
      <circle cx="22" cy="28" r="5.2" fill="rgba(255,255,255,0.22)" />
      <circle cx="44" cy="28" r="5.2" fill="rgba(255,255,255,0.18)" />
      <circle className="ai-eye" cx="24" cy="28" r="3.2" fill="#0B1220" />
      <circle className="ai-eye" cx="40" cy="28" r="3.2" fill="#0B1220" />
      <path
        className="ai-mouth"
        d="M22 40 C 27 46, 37 46, 42 40"
        fill="none"
        stroke="#0B1220"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
};

// 装修行业规范正则表达式
const STANDARD_PATTERNS = [
  { pattern: /GB\s*\d{5}(?:-\d{4})?/gi, label: '国家标准' },
  { pattern: /JGJ\s*\d{3}(?:-\d{4})?/gi, label: '行业标准' },
  { pattern: /防水高度\s*\d+cm/gi, label: '防水规范' },
  { pattern: /\d+cm\s*防水/gi, label: '防水规范' },
  { pattern: /平整度\s*[≤≤]?\s*\d+mm/gi, label: '施工规范' },
  { pattern: /间距\s*\d+mm/gi, label: '施工规范' },
  { pattern: /垂直度\s*[≤≤]?\s*\d+mm/gi, label: '施工规范' },
];

interface ChatAreaProps {
  theme: 'light' | 'dark';
  systemSettings?: SystemSettings | null;
}

const ChatArea: React.FC<ChatAreaProps> = ({ theme, systemSettings }) => {
  const {
    conversations,
    currentConversationId,
    streamingContent,
    isStreaming,
    sidebarLeftCollapsed,
  } = useChatStore();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const [animatingAvatarMessageId, setAnimatingAvatarMessageId] = useState<string | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const avatarAnimTimerRef = useRef<number | null>(null);
  const prevConversationIdRef = useRef<string | null>(null);
  const justSwitchedConversationRef = useRef(false);

  const currentConversation = conversations.find(
    (conv) => conv.id === currentConversationId
  );

  const messages = currentConversation?.messages || [];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // 计算相对于屏幕中心或视口的偏移，用于眼睛转动
      const x = (e.clientX / window.innerWidth - 0.5) * 2; // -1 to 1
      const y = (e.clientY / window.innerHeight - 0.5) * 2; // -1 to 1
      setMousePos({ x, y });
      
      // 更新全局 CSS 变量，让所有 AI 头像都能响应
      document.documentElement.style.setProperty('--mouse-x', x.toString());
      document.documentElement.style.setProperty('--mouse-y', y.toString());
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (avatarAnimTimerRef.current != null) {
        window.clearTimeout(avatarAnimTimerRef.current);
      }
      speechService.stop();
    };
  }, []);

  const triggerAvatarAnimation = (messageId: string) => {
    setAnimatingAvatarMessageId(messageId);
    if (avatarAnimTimerRef.current != null) {
      window.clearTimeout(avatarAnimTimerRef.current);
    }
    avatarAnimTimerRef.current = window.setTimeout(() => {
      setAnimatingAvatarMessageId((current) => (current === messageId ? null : current));
    }, 950);
  };

  // 实际滚动的是外层 .chat-content-wrapper（Home 布局），需用它判断是否贴底并滚动到底
  const getScrollContainer = (): HTMLElement | null => {
    const wrapper = document.querySelector('.chat-content-wrapper') as HTMLElement | null;
    if (wrapper) return wrapper;
    return messagesContainerRef.current;
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const el = getScrollContainer();
    if (!el) return;
    
    if (behavior === 'auto') {
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
    setIsPinnedToBottom(true);
  };

  const computeIsAtBottom = () => {
    const el = getScrollContainer();
    if (!el) return true;
    const thresholdPx = 80;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;
  };

  // 监听实际滚动的容器（外层 .chat-content-wrapper），未贴底时显示向下箭头
  useEffect(() => {
    const el = document.querySelector('.chat-content-wrapper') as HTMLElement | null;
    if (!el) return;

    const handleScroll = () => {
      const thresholdPx = 80;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;
      setIsPinnedToBottom(atBottom);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    // 初始及内容变化后检查一次（用短延迟确保 DOM 已更新）
    const t = window.setTimeout(handleScroll, 100);
    return () => {
      window.clearTimeout(t);
      el.removeEventListener('scroll', handleScroll);
    };
  }, [currentConversationId, messages.length]);

  // 当切换对话（或首次选择对话）时，停止朗读并回到顶部
  useEffect(() => {
    if (prevConversationIdRef.current !== currentConversationId) {
      speechService.stop();
      setSpeakingMessageId(null);
      justSwitchedConversationRef.current = true;
      setIsPinnedToBottom(true);
      // 消息列表容器回到顶部
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }
      // 右侧主滚动区域（.chat-content-wrapper 或 .home-welcome-area）回到顶部
      const contentWrapper = document.querySelector('.chat-content-wrapper');
      if (contentWrapper) {
        contentWrapper.scrollTop = 0;
      }
      const welcomeArea = document.querySelector('.home-welcome-area');
      if (welcomeArea) {
        welcomeArea.scrollTop = 0;
      }
      setTimeout(() => {
        justSwitchedConversationRef.current = false;
      }, 300);
    }
    prevConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // 当有新消息或流式内容时，自动滚动到底部（仅在当前对话内）
  useEffect(() => {
    // 如果刚刚切换了对话，不滚动到底部
    if (justSwitchedConversationRef.current) {
      return;
    }
    
    // 如果正在流式响应，滚动到底部
    if (isStreaming && streamingContent && isPinnedToBottom) {
      // 移动端生成内容时，强制滚动到底部，避免页面停留在顶部
      // 使用 'auto' 避免 smooth 动画在频繁更新时造成的跳动
      scrollToBottom('auto');
    }
  }, [streamingContent, isStreaming, isPinnedToBottom]);

  // 当消息列表变化时，如果不在流式响应中且不是刚切换对话，滚动到底部（用于新消息）
  useEffect(() => {
    // 如果刚刚切换了对话，不滚动到底部
    if (justSwitchedConversationRef.current) {
      return;
    }
    
    if (!isStreaming && messages.length > 0 && isPinnedToBottom) {
      // 延迟一下，确保DOM已更新
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 100);
    }
  }, [messages.length, isStreaming, isPinnedToBottom]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      antdMessage.success('已复制到剪贴板');
    } catch (err) {
      antdMessage.error('复制失败');
    }
  };

  const handleToggleSpeech = (messageId: string, content: string) => {
    if (speakingMessageId === messageId) {
      speechService.stop();
      setSpeakingMessageId(null);
    } else {
      setSpeakingMessageId(messageId);
      speechService.speak(content, () => {
        setSpeakingMessageId(null);
      });
    }
  };

  // 高亮装修行业规范 - 处理文本节点
  const highlightTextNode = (text: string): React.ReactNode => {
    if (typeof text !== 'string') return text;
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const matches: Array<{ start: number; end: number; label: string; match: string }> = [];

    // 收集所有匹配
    STANDARD_PATTERNS.forEach(({ pattern, label }) => {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          label,
          match: match[0],
        });
      }
    });

    // 按位置排序
    matches.sort((a, b) => a.start - b.start);

    // 去重重叠的匹配
    const nonOverlapping: typeof matches = [];
    matches.forEach((match) => {
      if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
        nonOverlapping.push(match);
      }
    });

    // 构建React节点
    nonOverlapping.forEach(({ start, end, label, match }) => {
      if (start > lastIndex) {
        parts.push(text.substring(lastIndex, start));
      }
      parts.push(
        <span key={`${start}-${end}`} className="decoration-standard" data-label={label} title={label}>
          {match}
        </span>
      );
      lastIndex = end;
    });

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 1 ? <>{parts}</> : text;
  };

  const renderMessage = (message: Message, index: number) => {
    const isUser = message.role === 'user';
    const isLastAssistant = !isUser && index === messages.length - 1;
    // 如果是最后一条助手消息且正在流式响应，显示流式内容
    const showStreamingContent = isLastAssistant && isStreaming && streamingContent;
    // 如果消息内容为空但正在流式响应，也显示流式内容
    const isEmptyAndStreaming = !isUser && !message.content && isStreaming && streamingContent;
    const isSpeaking = speakingMessageId === message.id;

    return (
      <div key={message.id} className={`message-item ${isUser ? 'user' : 'assistant'} ${isSpeaking ? 'is-speaking' : ''}`}>
        {!isUser && (
          <div className="message-avatar">
            <button
              type="button"
              className={`ai-avatar-btn ${animatingAvatarMessageId === message.id ? 'is-animating' : ''} ${isSpeaking ? 'speaking-wave' : ''}`}
              onClick={() => triggerAvatarAnimation(message.id)}
              aria-label={(systemSettings?.site_name || '米迦同学') + '头像'}
              title="点我一下"
            >
              <span className="ai-avatar">
                {systemSettings?.ai_icon_url ? (
                  <img 
                    src={getMediaUrl(systemSettings.ai_icon_url)} 
                    alt="AI" 
                    style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} 
                  />
                ) : (
                  <svg className="interactive-avatar" viewBox="0 0 64 64" width="40" height="40" aria-hidden="true">
                    <defs>
                      <linearGradient id="mijiaFace" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#6EE7B7" />
                        <stop offset="100%" stopColor="#22C55E" />
                      </linearGradient>
                    </defs>
                    <rect x="2" y="2" width="60" height="60" rx="16" fill="url(#mijiaFace)" />
                    
                    {/* 眉毛 */}
                    <path className={`ai-eyebrow left ${isSpeaking ? 'eyebrow-speak' : ''}`} d="M18 20 Q 24 18, 30 20" fill="none" stroke="#0B1220" strokeWidth="2.5" strokeLinecap="round" />
                    <path className={`ai-eyebrow right ${isSpeaking ? 'eyebrow-speak' : ''}`} d="M34 20 Q 40 18, 46 20" fill="none" stroke="#0B1220" strokeWidth="2.5" strokeLinecap="round" />
                    
                    <circle cx="22" cy="28" r="5.2" fill="rgba(255,255,255,0.22)" />
                    <circle cx="44" cy="28" r="5.2" fill="rgba(255,255,255,0.18)" />
                    <circle className={`ai-eye ${isSpeaking ? 'eye-blink' : ''}`} cx="24" cy="28" r="3.2" fill="#0B1220" />
                    <circle className={`ai-eye ${isSpeaking ? 'eye-blink' : ''}`} cx="40" cy="28" r="3.2" fill="#0B1220" />
                    <path
                      className={`ai-mouth ${isSpeaking ? 'mouth-speak' : ''}`}
                      d="M22 40 C 27 46, 37 46, 42 40"
                      fill="none"
                      stroke="#0B1220"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </span>
              {isSpeaking && (
                <div className="speaking-indicator-rings">
                  <div className="ring ring-1"></div>
                  <div className="ring ring-2"></div>
                </div>
              )}
            </button>
          </div>
        )}
        <div className="message-content-wrapper">
          <div className="message-header">
            <span className="message-role">{isUser ? user?.username || '用户' : (systemSettings?.site_name || '米迦同学')}</span>
            <div className="message-actions">
              {!isUser && (message.content || streamingContent) && (
                <Button
                  type="text"
                  size="small"
                  icon={isSpeaking ? <StopOutlined style={{ color: '#10b981' }} /> : <SoundOutlined />}
                  onClick={() => handleToggleSpeech(message.id, message.content || streamingContent || '')}
                  title={isSpeaking ? "停止朗读" : "朗读"}
                  className={isSpeaking ? 'btn-speaking' : ''}
                />
              )}
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(message.content || streamingContent || '')}
                title="复制"
              />
            </div>
          </div>
          <div className={`message-content ${isUser ? 'user-content' : 'assistant-content'}`}>
            {isUser ? (
              <div className="message-text">{message.content}</div>
            ) : (
              <>
                {/* 显示已保存的消息内容（如果有） */}
                {message.content && !showStreamingContent && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={theme === 'dark' ? vscDarkPlus : vs}
                            language={language}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // 自定义文本渲染以支持高亮
                      text({ children }: any) {
                        if (typeof children === 'string') {
                          return highlightTextNode(children);
                        }
                        return children;
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
                {/* 显示流式内容（实时更新） */}
                {(showStreamingContent || isEmptyAndStreaming) && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={theme === 'dark' ? vscDarkPlus : vs}
                            language={language}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // 自定义文本渲染以支持高亮
                      text({ children }: any) {
                        if (typeof children === 'string') {
                          return highlightTextNode(children);
                        }
                        return children;
                      },
                    }}
                  >
                    {streamingContent}
                  </ReactMarkdown>
                )}
                {/* 显示打字指示器 */}
                {isLastAssistant && isStreaming && (
                  <span className="typing-indicator">▋</span>
                )}

                {/* 显示图片和视频 */}
                {!isUser && (message.images || message.videos) && (
                  <div className="message-media-container">
                    {message.images && (
                      <div className="message-images">
                        <Image.PreviewGroup>
                          {message.images.split(/[\n,]/).filter(url => url.trim()).map((url, idx) => (
                            <Image
                              key={idx}
                              src={getMediaUrl(url.trim())}
                              width={120}
                              height={120}
                              className="message-image-item"
                              style={{ objectFit: 'cover', borderRadius: '8px' }}
                            />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}
                    {message.videos && (
                      <div className="message-videos">
                        {message.videos.split(/[\n,]/).filter(url => url.trim()).map((url, idx) => (
                          <div key={idx} className="video-wrapper">
                            <video
                              src={getMediaUrl(url.trim())}
                              controls
                              className="message-video-item"
                              style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '8px' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <div 
        className={`welcome-logo ${animatingAvatarMessageId === 'welcome' ? 'is-animating' : ''}`}
        onClick={() => triggerAvatarAnimation('welcome')}
        title="点我一下"
      >
        {systemSettings?.ai_icon_url ? (
          <img src={getMediaUrl(systemSettings.ai_icon_url)} alt="AI" className="welcome-avatar-img" />
        ) : (
          <MijiaAvatarSvg size={80} interactive={true} />
        )}
      </div>
      <h1>
        {systemSettings?.site_name ? `欢迎使用 ${systemSettings.site_name}` : '欢迎使用米迦同学'}
      </h1>
      <p>您的智能装修助手，随时为您解答装修难题</p>
    </div>
  );

  if (!currentConversation) {
    return (
      <div className="chat-area empty">
        {renderEmptyState()}
      </div>
    );
  }

  return (
    <div 
      className="chat-area"
      ref={chatContainerRef}
    >
      <div className={`chat-messages ${messages.length === 0 ? 'centered' : ''}`} ref={messagesContainerRef}>
        {messages.length === 0 ? (
          renderEmptyState()
        ) : (
          messages.map((message, index) => renderMessage(message, index))
        )}
        <div ref={messagesEndRef} />
      </div>

      {!isPinnedToBottom && (
        <Button
          className="scroll-to-bottom-btn"
          type="primary"
          shape="circle"
          icon={<DownOutlined />}
          onClick={() => {
            setIsPinnedToBottom(true);
            scrollToBottom('smooth');
          }}
          title="回到底部"
          aria-label="回到底部"
        />
      )}
    </div>
  );
};

export default ChatArea;
